<template>
  <div class="w-full">
    <div class="relative w-full space-y-1.5">
      <PebbleAppFormLabel
        v-if="label"
        :label="label"
        :required="required"
        :id="id"
        :class="['text-text-strong-dark font-medium ml-1', labelClassName]"
      />

      <div class="relative group">
        <div v-if="isMobileField" class="absolute top-0 left-0 h-full z-20">
          <Popover v-model:open="isDropdownOpen">
            <PopoverTrigger as-child>
              <button
                type="button"
                :disabled="disabled"
                class="flex items-center justify-center gap-1.5 h-full px-3 rounded-l-xl border-r border-white/5 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-text-strong-dark"
                :class="{ 'border-red-500/50': error }"
              >
                <span v-if="selectedCountry" class="flex items-center gap-1">
                  <span class="text-base leading-none">{{
                    selectedCountry.flag
                  }}</span>
                  <span class="text-sm font-bold">{{
                    selectedCountry.dialCode
                  }}</span>
                </span>
                <span v-else class="text-sm font-bold">+1</span>
                <ChevronDown class="w-3.5 h-3.5 opacity-50" />
              </button>
            </PopoverTrigger>

            <PopoverContent
              align="start"
              class="p-0 border border-white/10 bg-raised-dark w-64 overflow-hidden rounded-xl shadow-2xl"
            >
              <Command class="bg-transparent">
                <div class="flex items-center px-3 border-b border-white/5">
                  <Search class="w-4 h-4 opacity-40 mr-2" />
                  <input
                    v-model="searchQuery"
                    placeholder="Search countries..."
                    class="flex-1 h-10 bg-transparent text-sm outline-none placeholder:text-text-weak-dark/30 text-text-strong-dark"
                  />
                </div>
                <CommandEmpty
                  class="py-4 text-center text-xs text-text-weak-dark"
                >
                  No country found.
                </CommandEmpty>
                <CommandList class="max-h-64 overflow-y-auto custom-scrollbar">
                  <CommandGroup>
                    <CommandItem
                      v-for="country in filteredCountries"
                      :key="country.code"
                      :value="country.code"
                      class="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors hover:bg-white/5 aria-selected:bg-brand/10"
                      @select="selectCountry(country)"
                    >
                      <span class="text-lg">{{ country.flag }}</span>
                      <span
                        class="flex-1 text-sm text-text-strong-dark truncate"
                        >{{ country.name }}</span
                      >
                      <span class="text-xs font-mono text-brand">{{
                        country.dialCode
                      }}</span>
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div
          v-if="$slots.leftIcon && !isMobileField"
          class="absolute left-4 top-1/2 -translate-y-1/2 text-text-weak-dark z-10 pointer-events-none"
        >
          <slot name="leftIcon" />
        </div>

        <input
          :id="id"
          :type="computedType"
          :disabled="disabled"
          :readonly="readonly"
          :placeholder="placeholder"
          :value="modelValue"
          :tabindex="tabindex"
          @input="updateValue"
          class="w-full h-13 bg-fill-dark text-text-strong-dark placeholder:text-text-weak-dark/30 text-sm rounded-xl border transition-all outline-none"
          :class="[
            isMobileField ? 'pl-28' : $slots.leftIcon ? 'pl-11' : 'pl-4',
            isPasswordField || $slots.rightIcon ? 'pr-11' : 'pr-4',
            error
              ? 'border-red-500/50 focus:border-red-500 focus:ring-4 focus:ring-red-500/10'
              : 'border-white/5 focus:border-brand/50 focus:ring-4 focus:ring-brand/10 hover:border-white/10',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
            className,
          ]"
        />

        <div
          class="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10"
        >
          <slot v-if="$slots.rightIcon" name="rightIcon" />

          <button
            v-if="isPasswordField"
            type="button"
            class="p-1 hover:text-brand transition-colors focus:outline-none text-text-weak-dark"
            @click="showPassword = !showPassword"
          >
            <Eye v-if="!showPassword" class="w-5 h-5" />
            <EyeOff v-else class="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        v-if="hint && !error"
        class="flex items-center gap-1.5 px-1 pt-1 opacity-70"
      >
        <Info class="w-3.5 h-3.5 text-brand" />
        <span class="text-xs text-text-weak-dark font-light">
          {{ hint }}
        </span>
      </div>

      <PebbleAppFormLabelError
        v-if="error"
        :error="error"
        :class="['px-1 mt-1', errorClassName]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, useSlots } from "vue";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
  CommandItem,
} from "@/components/ui/command";
import { Eye, EyeOff, ChevronDown, Search, Info } from "lucide-vue-next";
import type { Country } from "@/assets/data/countries";
import { countries, findCountryByDialCode } from "@/assets/data/countries";

interface InputProps {
  type?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  label?: string;
  error?: string;
  hint?: string;
  tabindex?: number;
  labelClassName?: string;
  errorClassName?: string;
  isPasswordField?: boolean;
  isMobileField?: boolean;
  id?: string;
  modelValue?: string | number;
  countryCode?: string;
}

const props = withDefaults(defineProps<InputProps>(), {
  type: "text",
  disabled: false,
  readonly: false,
  required: false,
  placeholder: "",
  className: "",
  labelClassName: "",
  label: "",
  error: "",
  hint: "",
  tabindex: 0,
  isPasswordField: false,
  isMobileField: false,
  modelValue: "",
  countryCode: "+1",
});

const emit = defineEmits(["update:modelValue", "update:countryCode"]);
const slots = useSlots();

const id = computed(
  () => props.id || `input-${Math.random().toString(36).slice(2, 9)}`,
);

const showPassword = ref(false);
const searchQuery = ref("");
const selectedCountry = ref<Country | null>(null);
const isDropdownOpen = ref(false);

watch(
  () => props.countryCode,
  (newCode) => {
    const country = findCountryByDialCode(newCode);
    if (country) selectedCountry.value = country;
  },
  { immediate: true },
);

const filteredCountries = computed(() => {
  if (!searchQuery.value) return countries;
  const q = searchQuery.value.toLowerCase();
  return countries.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q) ||
      c.code.toLowerCase().includes(q),
  );
});

const computedType = computed(() => {
  if (props.isPasswordField) return showPassword.value ? "text" : "password";
  if (props.isMobileField) return "tel";
  return props.type;
});

const updateValue = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit("update:modelValue", target.value);
};

const selectCountry = (country: Country) => {
  selectedCountry.value = country;
  emit("update:countryCode", country.dialCode);
  isDropdownOpen.value = false;
  searchQuery.value = "";
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}
</style>
